import { z } from 'zod';
import { INGESTION_MAX_EVENTS } from '../api/ingestion.dto.js';
import {
  EVENT_PAYLOAD_KEYS,
  EVENT_TYPES,
  FEEDBACK_OPTIONAL_KEYS,
  FEEDBACK_REQUIRED_KEYS,
} from './event-type.enum.js';

const PAYLOAD_STRING_MAX_LENGTH = 64;

/**
 * Shape constraints (technical §25.3): every string field has a closed
 * alphabet, not just a type. No separator of a query string, a fragment or a
 * mailbox can appear in a path, a signature or a key.
 */
/** Normalised URL path: segments, `:id` / `:uuid` placeholders, percent escapes. */
export const PAGE_PATH_PATTERN = /^\/[A-Za-z0-9._~:@%/-]*$/;
/** A page path, optionally followed by `#` and a declared sub-step identifier. */
export const STEP_SIGNATURE_PATTERN = /^\/[A-Za-z0-9._~:@%/-]*(#[A-Za-z0-9._-]{1,64})?$/;
/** Technical key of a field (id, name, ARIA label, test id): letters, digits, light punctuation, spaces. */
export const FIELD_KEY_PATTERN = /^[\p{L}\p{N} _.:*[\]-]{1,128}$/u;
/**
 * CSS selector of the element the visitor touched, so a guidance can be anchored
 * to something the measurement actually saw (technical §7.2). Restricted to an
 * id or an attribute selector built by the SDK from the element's own id, name
 * or test id — never a path through the document, which would carry structure a
 * page can rebuild around a person.
 */
export const ELEMENT_SELECTOR_PATTERN = /^(#[A-Za-z0-9_:.-]{1,96}|\[[a-z-]{1,24}="[A-Za-z0-9_:.-]{1,96}"\])$/;
/** Native tag or input type of the element: `file`, `text`, `select`, `button`. */
export const ELEMENT_TYPE_PATTERN = /^[a-z][a-z0-9-]{0,31}$/;
/**
 * Visible label of the element, so a team recognises the field it is anchoring
 * to. This is text the administration wrote in its own form, never text a
 * visitor typed — the SDK never reads `element.value`. Kept short and refused
 * outright when it carries a mailbox, a phone number or an identity number, the
 * three shapes a label should never contain (technical §16.4, §25.3).
 */
export const ELEMENT_LABEL_MAX_LENGTH = 128;
export const PERSONAL_DATA_SHAPES: readonly RegExp[] = [
  /[\w.+-]+@[\w-]+\.[\w.-]+/u,
  /(?:\+?\d[\d\s.-]{7,})/u,
  /\b[A-Za-z]{1,2}\d{5,}\b/u,
];
/** Lengths of the optional feedback comment (ADR 0016); mirrored by the domain. */
export const FEEDBACK_COMMENT_TITLE_MAX_LENGTH = 80;
export const FEEDBACK_COMMENT_BODY_MAX_LENGTH = 500;

/**
 * The optional comment of a feedback answer (functional §6.8, brought forward by
 * ADR 0016): the only free text an event may carry, on `feedback_given` only,
 * already masked by the SDK and masked again at reception.
 */
export const feedbackCommentSchema = z
  .object({
    title: z.string().max(FEEDBACK_COMMENT_TITLE_MAX_LENGTH).nullable().default(null),
    body: z.string().max(FEEDBACK_COMMENT_BODY_MAX_LENGTH).nullable().default(null),
  })
  .strict();
export type FeedbackCommentInput = z.infer<typeof feedbackCommentSchema>;

/** 128 random bits, hexadecimal (technical §16.4 rule 6). */
export const SESSION_PSEUDONYM_PATTERN = /^[0-9a-f]{32}$/;
/**
 * Every feedback key (prompt, answer, announcement, type, entity): a token from a
 * closed alphabet, at most 64 characters. A UUID, a slug or a grade fits; a
 * sentence, a mailbox or a phone number cannot.
 */
export const FEEDBACK_KEY_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_.:-]{0,63}$/;
/** Device classes the SDK derives from the viewport width (functional §6.6 "contexte technique"). */
export const VIEWPORT_CLASSES = ['mobile', 'tablet', 'desktop'] as const;
export type ViewportClass = (typeof VIEWPORT_CLASSES)[number];

/**
 * Closed event schema. No field may ever carry a typed value, a rendered
 * message, a file content or a network address (doc technique §25.3). The
 * payload keys are whitelisted per type (§16.4, rule 1).
 */
export const journeyEventSchema = z
  .object({
    eventId: z.string().uuid(),
    type: z.enum(EVENT_TYPES),
    occurredAt: z.string().datetime(),
    sessionPseudonym: z.string().regex(SESSION_PSEUDONYM_PATTERN),
    /** Normalised URL path. Query string and fragment are stripped by the SDK. */
    pagePath: z.string().max(512).regex(PAGE_PATH_PATTERN),
    /** Stable signature of the observed step, computed browser-side. */
    stepSignature: z.string().max(128).regex(STEP_SIGNATURE_PATTERN),
    /** Technical field key (id/name/role), never a value. */
    fieldKey: z.string().max(128).regex(FIELD_KEY_PATTERN).nullable().default(null),
    /**
     * The element the visitor touched, so the analysis can offer real anchors to
     * a guidance instead of asking a team to invent a selector (technical §7.2).
     * Absent on every event that is not about one element.
     */
    elementSelector: z.string().regex(ELEMENT_SELECTOR_PATTERN).nullable().default(null),
    elementType: z.string().regex(ELEMENT_TYPE_PATTERN).nullable().default(null),
    elementLabel: z
      .string()
      .max(ELEMENT_LABEL_MAX_LENGTH)
      .refine((label) => !PERSONAL_DATA_SHAPES.some((shape) => shape.test(label)), {
        message: 'elementLabel must not carry a mailbox, a phone number or an identity number',
      })
      .nullable()
      .default(null),
    language: z.string().length(2).nullable().default(null),
    viewport: z.enum(VIEWPORT_CLASSES),
    /** Numeric, boolean or short enum values only, keys constrained per type. */
    payload: z
      .record(z.union([z.number(), z.boolean(), z.string().max(PAYLOAD_STRING_MAX_LENGTH)]))
      .default({}),
    /** Only on `feedback_given`, only when the question allows it (ADR 0016). */
    comment: feedbackCommentSchema.nullable().default(null),
  })
  .strict()
  .superRefine((event, context) => {
    const allowed = EVENT_PAYLOAD_KEYS[event.type];
    for (const key of Object.keys(event.payload)) {
      if (!allowed.includes(key)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['payload', key],
          message: `payload key "${key}" is not allowed for event type "${event.type}"`,
        });
      }
    }
    if (event.comment !== null && event.type !== 'feedback_given') {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['comment'],
        message: 'only a feedback answer may carry a comment',
      });
    }
    if (event.type !== 'feedback_given') return;
    // A feedback is stored on its own table: its keys must exist and be tokens (technical §25.3).
    for (const key of FEEDBACK_REQUIRED_KEYS) {
      const value = event.payload[key];
      if (typeof value !== 'string' || !FEEDBACK_KEY_PATTERN.test(value)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['payload', key],
          message: `payload key "${key}" must be a feedback key`,
        });
      }
    }
    for (const key of FEEDBACK_OPTIONAL_KEYS) {
      const value = event.payload[key];
      if (value !== undefined && (typeof value !== 'string' || !FEEDBACK_KEY_PATTERN.test(value))) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['payload', key],
          message: `payload key "${key}" must be a feedback key`,
        });
      }
    }
  });

export type JourneyEvent = z.infer<typeof journeyEventSchema>;

export const eventEnvelopeSchema = z
  .object({
    projectKey: z.string().min(16).max(64),
    sdkVersion: z.string().max(16),
    schemaVersion: z.literal(1),
    sentAt: z.string().datetime(),
    /** Marks demo sessions produced through the Extension. Excluded from metrics. */
    internalSession: z.boolean().default(false),
    events: z.array(journeyEventSchema).min(1).max(INGESTION_MAX_EVENTS),
  })
  .strict();

export type EventEnvelope = z.infer<typeof eventEnvelopeSchema>;
